import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Squad } from '../../../types';
import { useCurriculumAdapter } from '../../curriculum/hooks/useCurriculumAdapter';
import { getSquadCurriculumId, isMusicCurriculum } from '../utils/curriculum';
import { startLevel, finishClass, updateMissionProgress, cancelClass } from '../../../services/firebase';
import FeedbackModal from './FeedbackModal';
import { ActivityModal } from '../../curriculum/components/ActivityModal';
import { useRealTimeAchievementProgress } from '../hooks/useRealTimeAchievementProgress';
import { EnhancedProgressBar } from './EnhancedProgressBar';
import { useAuth } from '../../auth/contexts/AuthContext';
import { gameAPI, AchievementDefinition } from '../../games/services/GameAPI';
import { collection, onSnapshot, query, where, documentId } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import SquadBoardModeModal from './SquadBoardModeModal';
import { User } from '../../../types';
import { getGameById } from '../../games/models/GameRegistry';
import { toMsSafe } from '../../../shared/utils/firestoreDates';

interface NextClassSectionProps {
  squad: Squad;
  selectedLevelId: string;
  onSquadUpdate?: () => void;
  onTakeAttendance: () => void;
}

const NextClassSection: React.FC<NextClassSectionProps> = ({ 
  squad, 
  selectedLevelId, 
  onSquadUpdate,
  onTakeAttendance,
}) => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const adapter = useCurriculumAdapter({ squad });
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showMissionModal, setShowMissionModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<any | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAttendanceTaken, setIsAttendanceTaken] = useState(false);
  const [missionAchievements, setMissionAchievements] = useState<Record<string, AchievementDefinition[]>>({});
  const [reflectSubmissionsCount, setReflectSubmissionsCount] = useState<number>(0);
  const [isBoardModeOpen, setIsBoardModeOpen] = useState(false);
  const [students, setStudents] = useState<User[]>([]);
  const [selectedMissionId, setSelectedMissionId] = useState<string | null>(null);

  // Per-squad overrides
  const TARGET_SQUAD_TITLES = new Set<string>([
    'Winnetka Community House Fall A',
    'NSCD Fall A',
    'John Laidlaw Fall A',
    'Chiaravalle Fall A',
  ]);

  const isTargetSquad = useMemo(() => {
    const title = (squad?.title || '').trim();
    if (!title) return false;
    // exact match or contains school name with "Fall A" suffix
    if (TARGET_SQUAD_TITLES.has(title)) return true;
    // Fallback partial check
    return (
      /Fall A/i.test(title) && (
        /Winnetka Community House/i.test(title) ||
        /NSCD/i.test(title) ||
        /John Laidlaw/i.test(title) ||
        /Chiaravalle/i.test(title)
      )
    );
  }, [squad?.title]);

  const baseLevel = adapter.getLevelById(selectedLevelId) as any;
  const selectedLevel = useMemo(() => {
    if (!baseLevel) return baseLevel;
    const missions = baseLevel.missions || [];
    const withFlyers = isTargetSquad
      ? [
          ...missions,
          {
            type: 'completion',
            id: 'give-out-flyers',
            text: 'Give out flyers',
            completed: false,
          },
        ]
      : missions;
    return { ...baseLevel, missions: withFlyers };
  }, [baseLevel, isTargetSquad]);
  const isActiveClass = squad.classStatus === 'current';
  const isMusicClass = isMusicCurriculum(squad);
  const isReplay = (squad.completedLevels || []).includes(selectedLevelId);

  const { achievementProgress, userAchievementCounts } = useRealTimeAchievementProgress(
    squad,
    selectedLevel || null,
    isActiveClass
  );

  // Load achievements for all measurable missions
  useEffect(() => {
    const loadAchievements = async () => {
      if (!selectedLevel) return;
      
      const achievements: Record<string, AchievementDefinition[]> = {};
      
      for (const mission of selectedLevel.missions) {
        if (mission.type === 'measurable' && mission.gameId) {
          try {
            const defs = await gameAPI.getGameAchievementDefinitions(mission.gameId);
            achievements[mission.id] = defs;
          } catch (error) {
            console.error(`Failed to load achievements for ${mission.gameId}:`, error);
            achievements[mission.id] = [];
          }
        }
      }
      
      setMissionAchievements(achievements);
    };

    
    loadAchievements();
  }, [selectedLevel]);

  useEffect(() => {
    if (isActiveClass) {
      const lastAttendanceDate = squad.attendanceMetadata?.lastTaken;
      if (lastAttendanceDate) {
        const attendanceTime = new Date(lastAttendanceDate).getTime();
        const sessionStartTime = toMsSafe(squad.liveSession?.startTime) || 0;
        if (sessionStartTime && attendanceTime >= sessionStartTime) {
          setIsAttendanceTaken(true);
        } else {
          setIsAttendanceTaken(false);
        }
      } else {
        setIsAttendanceTaken(false);
      }
    } else {
      setIsAttendanceTaken(false);
    }
  }, [squad.attendanceMetadata, squad.liveSession, isActiveClass]);

  // Fetch students for Board Mode
  useEffect(() => {
    if (!squad?.studentIds || squad.studentIds.length === 0) {
      setStudents([]);
      return;
    }
    const studentsRef = collection(db, 'users');
    const studentsQuery = query(studentsRef, where(documentId(), 'in', squad.studentIds));
    const unsubscribe = onSnapshot(studentsQuery, (studentsSnapshot) => {
      const studentsData = studentsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as User));
      setStudents(studentsData);
    });
    return () => unsubscribe();
  }, [squad?.studentIds]);

  // Real-time progress for Reflect and Progress mission (studentFeedback submissions for current session/level)
  useEffect(() => {
    if (!isActiveClass || !squad.liveSession?.sessionId) {
      setReflectSubmissionsCount(0);
      return;
    }
    const sessionId = squad.liveSession.sessionId;
    const q = query(
      collection(db, 'studentFeedback'),
      where('sessionId', '==', sessionId),
      where('levelId', '==', selectedLevelId)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      setReflectSubmissionsCount(snap.size);
    });
    return () => unsubscribe();
  }, [isActiveClass, squad.liveSession, selectedLevelId]);

  const handleMissionToggle = async (missionId: string) => {
    if (!selectedLevel || !isActiveClass) return;
    setIsUpdating(true);
    try {
      await updateMissionProgress(squad.id, selectedLevelId, missionId, 'toggle');
      onSquadUpdate?.();
    } catch (error) {
      console.error('Error updating mission:', error);
    }
    setIsUpdating(false);
  };

  const handleMissionClick = (missionId: string) => {
    // Per-squad override: custom activity for flyers
    if (isTargetSquad && missionId === 'give-out-flyers') {
      const schoolName = squad?.locationName || squad?.location?.name || squad?.title || 'your school';
      const customActivity = {
        id: 'give-out-flyers-activity',
        title: 'Give out flyers',
        description: `Distribute the take-home flyers for ${schoolName}. Encourage students to share them with their families.`,
        duration: '2 minutes',
        difficulty: 'Easy',
        materials: ['Printed flyers specific to your school'],
        instructions: [
          `Give each student a flyer for ${schoolName}.`,
          'Ask them to place it carefully in their backpack and show it to a parent/guardian tonight.',
          'Optional: Highlight the QR code on the flyer for easy signup.',
        ],
      } as any;
      setSelectedActivity(customActivity);
      setSelectedMissionId(missionId);
      setShowActivityModal(true);
      return;
    }

    const mission = selectedLevel?.missions?.find((m: any) => m && m.id === missionId);
    const activity = adapter.getMissionActivity(missionId);
    const rawGameId = mission?.gameId ? String(mission.gameId) : '';

    // Ensure we still show a modal for game-based completion missions even if there is no activity entry.
    if (!activity && mission?.type === 'completion' && rawGameId) {
      setSelectedActivity({
        id: missionId,
        title: mission.text || 'Game',
        description: 'Open the game full screen for the class.',
        instructions: [
          'Click "Open Game" to launch in a new full-screen tab.',
        ],
      } as any);
      setSelectedMissionId(missionId);
      setShowActivityModal(true);
      return;
    }

    if (activity) {
      setSelectedActivity(activity);
      setSelectedMissionId(missionId);
      setShowActivityModal(true);
    }
  };

  const handleCloseActivityModal = () => {
    setShowActivityModal(false);
    setSelectedActivity(null);
    setSelectedMissionId(null);
  };

  const handleStartLevelClick = () => setShowStartModal(true);
  const handleConfirmStart = () => {
    setShowStartModal(false);
    setShowMissionModal(true);
  };

  const handleFinalStart = async () => {
    if (!currentUser) return;
    setShowMissionModal(false);
    setIsUpdating(true);
    try {
      const teacherName = userData?.displayName || currentUser?.displayName || 'Teacher';
      await startLevel(squad.id, selectedLevelId, currentUser.uid, teacherName);
      onSquadUpdate?.();
      setTimeout(() => onTakeAttendance(), 500);
    } catch (error) {
      console.error('Error starting level:', error);
    }
    setIsUpdating(false);
  };

  const handleCloseModals = () => {
    setShowStartModal(false);
    setShowMissionModal(false);
  };

  const handleFinishClassAfterFeedback = async () => {
    setIsUpdating(true);
    try {
      await finishClass(squad.id);
      onSquadUpdate?.();
      setIsFeedbackModalOpen(false);
    } catch (error) {
      console.error('Error finishing class:', error);
    }
    setIsUpdating(false);
  };

  const handleCancelSession = async (payload: { reason: 'accidental' | 'otherTeacher' | 'preview' | 'other'; otherText?: string }) => {
    if (!currentUser) return;
    setIsUpdating(true);
    try {
      await cancelClass(squad.id, {
        teacherId: currentUser.uid,
        reason: payload.reason,
        reasonOther: payload.otherText,
      });
      onSquadUpdate?.();
      setIsFeedbackModalOpen(false);
    } catch (error) {
      console.error('Error canceling class:', error);
    }
    setIsUpdating(false);
  };

  const getActiveStudentCount = () => {
    return squad.attendance ? Object.values(squad.attendance).filter(present => present).length : 0;
  };

  const calculateDynamicTarget = (baseMissionTarget: number) => {
    return getActiveStudentCount() * baseMissionTarget;
  };
  
  const getMissionProgress = (missionId: string): boolean | number => {
    if (selectedLevel) {
      const mission = selectedLevel.missions.find((m: any) => m.id === missionId);
      if (mission?.type === 'measurable' && mission.id.startsWith('reflect-and-progress-')) {
        return reflectSubmissionsCount;
      }
      if (mission?.type === 'measurable' && mission.gameId) {
        return achievementProgress[missionId] || 0;
      }
    }
    const levelProgress = squad.missionProgress?.[selectedLevelId] || {};
    return levelProgress[missionId] || 0;
  };

  const isFinalMissionCompleted = (): boolean => {
    if (!selectedLevel || selectedLevel.missions.length === 0) return false;
    const finalMission = selectedLevel.missions[selectedLevel.missions.length - 1];
    const progress = getMissionProgress(finalMission.id);
    if (finalMission.type === 'completion') return !!progress;
    return (progress as number) >= calculateDynamicTarget(finalMission.target);
  };

  const findMissionById = (missionId: string) => {
    const all = [
      ...(selectedLevel?.missions || []),
      ...(selectedLevel?.sideQuests || []),
    ];
    return all.find((m: any) => m && m.id === missionId);
  };

  const renderMission = (mission: any) => {
    const label = mission.text;
    return (
      <div key={mission.id} className={`mission-item clickable`} onClick={() => handleMissionClick(mission.id)}>
        {mission.type === 'completion' ? (
          <>
            <div className={`mission-bullet ${getMissionProgress(mission.id) ? 'completed' : ''}`} onClick={(e) => { e.stopPropagation(); handleMissionToggle(mission.id); }}>
              {getMissionProgress(mission.id) && <span className="mission-checkmark">✓</span>}
            </div>
            <span className="mission-text">{label}</span>
          </>
        ) : (
          <EnhancedProgressBar
            current={getMissionProgress(mission.id) as number}
            target={isActiveClass ? calculateDynamicTarget(mission.target) : mission.target}
            missionId={mission.id}
            label={label}
            unit={mission.unit}
            isActiveClass={isActiveClass}
            achievements={missionAchievements[mission.id] || []}
            gameId={mission.gameId}
          />
        )}
      </div>
    );
  };

  if (!selectedLevel) {
    return <div className="next-class-section"><p>Level not found</p></div>;
  }

  return (
    <>
      <div className={`next-class-section ${isActiveClass ? 'active-class' : ''}`}>
        <div className="class-details">
          <div className="world-level-info"><p>{selectedLevel.title}</p></div>

          {isActiveClass && isMusicClass && (
            <div className="action-buttons music-finish-action">
              <button className={`finish-class-btn ${isFinalMissionCompleted() ? 'flashing-button' : ''}`} onClick={() => setIsFeedbackModalOpen(true)} disabled={isUpdating}>
                Finish Level
              </button>
            </div>
          )}

          {isActiveClass && (
            <div className="action-buttons top-buttons">
              <button className={`take-attendance-btn ${!isAttendanceTaken ? 'flashing-button' : ''}`} onClick={onTakeAttendance} disabled={isUpdating}>
                Take Attendance
              </button>
            </div>
          )}

          {!isActiveClass && (
            <div className="action-buttons">
              <button className={isReplay ? "replay-level-btn" : "start-level-btn"} onClick={handleStartLevelClick} disabled={isUpdating}>
                {isUpdating ? 'Starting...' : isReplay ? 'Replay Level' : 'Start Level'}
              </button>
            </div>
          )}

          <div className="level-missions">
            <h4>TODAY'S MISSIONS:</h4>
            <div className="missions-list">{selectedLevel.missions.map(renderMission)}</div>
          </div>

          {isActiveClass && !isMusicClass && (
            <div className="action-buttons middle-buttons">
              <button className={`finish-class-btn ${isFinalMissionCompleted() ? 'flashing-button' : ''}`} onClick={() => setIsFeedbackModalOpen(true)} disabled={isUpdating}>
                Finish Level
              </button>
            </div>
          )}

          {selectedLevel.sideQuests && selectedLevel.sideQuests.length > 0 && (
            <div className="side-quests-section">
              <h4>SIDE QUESTS (OPTIONAL):</h4>
              <div className="missions-list">{selectedLevel.sideQuests.map(renderMission)}</div>
            </div>
          )}
        </div>
      </div>

      {userData && currentUser && (
        <FeedbackModal
          squad={squad}
          isOpen={isFeedbackModalOpen}
          onClose={() => setIsFeedbackModalOpen(false)}
          onSubmitted={handleFinishClassAfterFeedback}
          onCancelSession={handleCancelSession}
          teacherId={currentUser.uid}
          teacherName={userData.displayName || 'Teacher'}
          achievementProgress={userAchievementCounts}
        />
      )}

      {showStartModal && (
        <div className="start-level-modal-overlay" onClick={handleCloseModals}>
          <div className="start-level-modal" onClick={e => e.stopPropagation()}>
            <div className="start-level-modal-header"><h3>{isReplay ? 'Ready to Replay?' : 'Are you sure you are ready to start this level?'}</h3></div>
            <div className="start-level-modal-content"><div className="level-title-display">{selectedLevel.title}</div></div>
            <div className="start-level-modal-buttons">
              <button className="cancel-start-btn" onClick={handleCloseModals}>Not yet</button>
              <button className="confirm-start-btn" onClick={handleConfirmStart}>Yes</button>
            </div>
          </div>
        </div>
      )}

      {showMissionModal && (
        <div className="start-level-goal-modal-overlay" onClick={handleCloseModals}>
          <div className="start-level-goal-modal" onClick={e => e.stopPropagation()}>
            <div className="start-level-goal-modal-header">
              <h3>{isReplay ? 'Mission Replay' : 'Mission Briefing'}</h3>
              <div className="level-title-small">{selectedLevel.title}</div>
            </div>
            <div className="start-level-goal-modal-content">
              <div className="goal-section-modal">
                <h4>Your missions for this level:</h4>
                <ul>{selectedLevel.missions.map((m: any, i: number) => <li key={i}>{m.text}</li>)}</ul>
              </div>
              <p className="mission-question">Are you prepared for these missions today?</p>
            </div>
            <div className="start-level-goal-modal-buttons">
              <button className="cancel-mission-btn" onClick={handleCloseModals}>Not yet</button>
              <button className="lets-do-this-btn" onClick={handleFinalStart}>{isReplay ? "Let's replay!" : "Let's do this!"}</button>
            </div>
          </div>
        </div>
      )}

      {selectedActivity && (
        <ActivityModal
          activity={selectedActivity}
          isOpen={showActivityModal}
          onClose={handleCloseActivityModal}
          size="large"
          // Show the primary button for:
          // - measurable missions (progress-driven activities)
          // - completion missions that are games (have a gameId) so teachers can still "Open Game"
          // Hide it for non-game completion missions like Winter Kickoff video.
          showStartButton={(() => {
            const missionId = selectedMissionId || selectedActivity.id;
            const m = findMissionById(missionId);
            if (!m) return true; // fallback to existing behavior if mission isn't found
            return m.type === 'measurable' || (m.type === 'completion' && !!m.gameId);
          })()}
          primaryAction={(() => {
            const missionId = selectedMissionId || selectedActivity.id;
            const m = findMissionById(missionId);
            const rawGameId = m?.gameId ? String(m.gameId) : '';
            if (m?.type === 'completion' && rawGameId) {
              let toOpen = rawGameId;
              // If a student id is provided, prefer a teacher variant when available.
              if (toOpen.endsWith('-student')) {
                const teacherVariant = toOpen.replace(/-student$/, '-teacher');
                if (getGameById(teacherVariant)) toOpen = teacherVariant;
              }
              return {
                label: 'Open Game',
                onClick: () => {
                  handleCloseActivityModal();
                  window.open(`/play/${encodeURIComponent(toOpen)}`, '_blank', 'noopener,noreferrer');
                },
              };
            }
            return {
              label: 'Start Activity',
              onClick: () => {
                if (!selectedActivity) return;
                handleCloseActivityModal();
                const mid = selectedMissionId || selectedActivity.id;
                navigate(`/app/squads/${squad.id}/mission/${selectedLevelId}/${mid}`, {
                  state: {
                    activity: selectedActivity,
                    levelId: selectedLevelId,
                  },
                });
              },
            };
          })()}
          levelId={selectedLevelId}
          curriculumId={getSquadCurriculumId(squad)}
          onSendReflections={() => {
            setIsBoardModeOpen(true);
          }}
        />
      )}

      <SquadBoardModeModal
        squad={squad}
        students={students}
        isOpen={isBoardModeOpen}
        onClose={() => setIsBoardModeOpen(false)}
      />
    </>
  );
};

export default NextClassSection;
